import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';
import { MapPin, Bed, Bath, Square, Building2, Home, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RENT_RANGES = [
  { value: '', label: 'All Budgets' },
  { value: 'under_2000', label: 'Under $2,000/mo' },
  { value: '2000_5000', label: '$2,000 - $5,000/mo' },
  { value: '5000_10000', label: '$5,000 - $10,000/mo' },
  { value: 'over_10000', label: 'Over $10,000/mo' }
];

const BUY_RANGES = [
  { value: '', label: 'All Budgets' },
  { value: '250_500', label: '$250K - $500K' },
  { value: '500_1m', label: '$500K - $1M' },
  { value: '1m_3m', label: '$1M - $3M' },
  { value: '3m_10m', label: '$3M - $10M' },
  { value: 'over_10m', label: 'Over $10M' }
];

const CuratedListings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [intent, setIntent] = useState(searchParams.get('intent') || '');
  const [city, setCity] = useState(searchParams.get('city') || '');
  const [rentRange, setRentRange] = useState(searchParams.get('rent_range') || '');
  const [buyRange, setBuyRange] = useState(searchParams.get('buy_range') || '');

  useEffect(() => {
    fetchListings();
  }, [intent, city, rentRange, buyRange]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (intent) params.append('intent', intent);
      if (city) params.append('city', city);
      if (intent === 'rent' && rentRange) params.append('rent_range', rentRange);
      if (intent === 'buy' && buyRange) params.append('buy_range', buyRange);
      
      const response = await fetch(`${API_URL}/api/listings?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setListings(data.listings || []);
      }
    } catch (err) {
      console.error('Error fetching listings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    
    // Reset budget ranges when intent changes
    if (key === 'intent') {
      newParams.delete('rent_range');
      newParams.delete('buy_range');
      setRentRange('');
      setBuyRange('');
    }
    
    setSearchParams(newParams);
    
    if (key === 'intent') setIntent(value);
    if (key === 'city') setCity(value);
    if (key === 'rent_range') setRentRange(value);
    if (key === 'buy_range') setBuyRange(value);
  };

  const formatPrice = (amount, currency, intentType) => {
    if (!amount) return '';
    const formatted = `${currency === 'MXN' ? 'MX$' : '$'}${amount.toLocaleString()}`;
    return intentType === 'rent' ? `${formatted}/mo` : formatted;
  };

  return (
    <div className="min-h-screen bg-[#F9F6EF]">
      <SEO
        title="Curated Listings"
        description="Browse curated real estate listings in Mexico City - hand-picked properties for rent and sale by The Mundalo."
        path="/relocation/listings"
      />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-12 border-b border-[#FFFFFF]">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-[11px] text-[#555] uppercase tracking-[0.2em] mb-4">Curated Properties</p>
          <h1 className="text-4xl lg:text-5xl font-light text-white mb-4">
            Relocation Listings
          </h1>
          <p className="text-lg text-[#888] max-w-2xl">
            Hand-selected properties for cross-border clients. Each listing has been reviewed 
            for quality, location, and suitability for relocation.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 border-b border-[#FFFFFF]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* City Filter */}
            <div>
              <input
                type="text"
                value={city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
                placeholder="City..."
                className="bg-[#F9F6EF] border border-[#222] px-4 py-2.5 text-white text-sm placeholder-[#555] focus:border-[#2CA8A0] focus:outline-none w-40"
                data-testid="filter-city"
              />
            </div>

            {/* Intent Filter */}
            <div className="flex">
              <button
                onClick={() => handleFilterChange('intent', '')}
                className={`px-4 py-2.5 text-sm border border-r-0 border-[#222] transition ${
                  !intent ? 'bg-[#2CA8A0] text-black border-[#2CA8A0]' : 'bg-[#F9F6EF] text-[#888] hover:text-white'
                }`}
              >
                All
              </button>
              <button
                onClick={() => handleFilterChange('intent', 'rent')}
                className={`px-4 py-2.5 text-sm border border-r-0 border-[#222] transition ${
                  intent === 'rent' ? 'bg-[#2CA8A0] text-black border-[#2CA8A0]' : 'bg-[#F9F6EF] text-[#888] hover:text-white'
                }`}
                data-testid="filter-rent"
              >
                Rent
              </button>
              <button
                onClick={() => handleFilterChange('intent', 'buy')}
                className={`px-4 py-2.5 text-sm border border-[#222] transition ${
                  intent === 'buy' ? 'bg-[#2CA8A0] text-black border-[#2CA8A0]' : 'bg-[#F9F6EF] text-[#888] hover:text-white'
                }`}
                data-testid="filter-buy"
              >
                Buy
              </button>
            </div>

            {/* Budget Range */}
            {intent && (
              <select
                value={intent === 'rent' ? rentRange : buyRange}
                onChange={(e) => handleFilterChange(intent === 'rent' ? 'rent_range' : 'buy_range', e.target.value)}
                className="bg-[#F9F6EF] border border-[#222] px-4 py-2.5 text-white text-sm focus:border-[#2CA8A0] focus:outline-none"
                data-testid="filter-budget"
              >
                {(intent === 'rent' ? RENT_RANGES : BUY_RANGES).map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            )}

            <span className="text-[#666] text-sm ml-auto">
              {listings.length} {listings.length === 1 ? 'listing' : 'listings'}
            </span>
          </div>
        </div>
      </section>

      {/* Listings Grid */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#2CA8A0] animate-spin" />
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-20">
              <Building2 className="w-12 h-12 text-[#333] mx-auto mb-4" />
              <h3 className="text-xl text-white mb-2">No listings found</h3>
              <p className="text-[#666]">
                Try adjusting your filters or check back soon for new properties.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map((listing) => (
                <Link
                  key={listing.id}
                  to={`/relocation/listings/${listing.slug}`}
                  className="group bg-[#F9F6EF] border border-[#FFFFFF] hover:border-[#333] transition-all"
                  data-testid={`listing-card-${listing.id}`}
                >
                  {/* Image */}
                  <div className="aspect-[4/3] bg-[#111] relative overflow-hidden">
                    {listing.images?.[0] ? (
                      <img
                        src={`${API_URL}${listing.images[0].url}`}
                        alt={listing.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Home className="w-12 h-12 text-[#333]" />
                      </div>
                    )}
                    
                    {/* Intent Badge */}
                    <span className={`absolute top-4 left-4 px-2.5 py-1 text-xs font-medium ${
                      listing.intent === 'rent'
                        ? 'bg-blue-500 text-white'
                        : 'bg-green-500 text-white'
                    }`}>
                      {listing.intent === 'rent' ? 'For Rent' : 'For Sale'}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <h3 className="text-white font-medium group-hover:text-[#2CA8A0] transition line-clamp-1">
                        {listing.title}
                      </h3>
                      <p className="text-[#2CA8A0] font-medium shrink-0">
                        {formatPrice(listing.price_amount, listing.price_currency, listing.intent)}
                      </p>
                    </div>

                    {(listing.neighborhood || listing.city) && (
                      <div className="flex items-center gap-1.5 text-[#888] text-sm mb-4">
                        <MapPin className="w-3.5 h-3.5" />
                        <span>{listing.neighborhood ? `${listing.neighborhood}, ` : ''}{listing.city}</span>
                      </div>
                    )}

                    {/* Facts */}
                    <div className="flex items-center gap-4 text-[#666] text-sm">
                      {listing.bedrooms && (
                        <div className="flex items-center gap-1.5">
                          <Bed className="w-4 h-4" />
                          <span>{listing.bedrooms}</span>
                        </div>
                      )}
                      {listing.bathrooms && (
                        <div className="flex items-center gap-1.5">
                          <Bath className="w-4 h-4" />
                          <span>{listing.bathrooms}</span>
                        </div>
                      )}
                      {listing.area_sqm && (
                        <div className="flex items-center gap-1.5">
                          <Square className="w-4 h-4" />
                          <span>{listing.area_sqm} m²</span>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CuratedListings;
